'use client';
import React, { useState, useEffect } from 'react';
import { Search, Bell, ChevronDown, Wifi, WifiOff, LogOut, Clock, Radio } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { getMarketSession, getISTTimeString, type MarketSession } from '@/lib/india-market';
import { useMarketData } from '@/contexts/MarketDataContext';

const SESSION_LABELS: Record<MarketSession, string> = {
  OPEN:       'MARKET OPEN',
  PRE_OPEN:   'PRE-MARKET 9:00–9:07',
  POST_CLOSE: 'POST-CLOSE',
  CLOSED:     'MARKET CLOSED',
};

const SESSION_STYLES: Record<MarketSession, string> = {
  OPEN:       'bg-positive-subtle text-positive',
  PRE_OPEN:   'bg-warning/10 text-warning',
  POST_CLOSE: 'bg-info-subtle text-info',
  CLOSED:     'bg-muted text-muted-foreground',
};

export default function Topbar() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [session, setSession] = useState<MarketSession>('CLOSED');
  const [istTime, setIstTime] = useState('');

  const { user, signOut } = useAuth();
  const router = useRouter();
  const { ticks, indices, isLive } = useMarketData();

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setSession(getMarketSession(now));
      setIstTime(getISTTimeString(now));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Periodic trigger for Upstox Edge Function (every 20s during market hours) ──
  useEffect(() => {
    if (session !== 'OPEN' && session !== 'PRE_OPEN') return;

    const triggerFeed = async () => {
      try {
        await fetch('/api/market-feed/trigger', { method: 'POST' });
      } catch {
        // silently ignore — simulation fallback is active
      }
    };

    triggerFeed(); // trigger immediately on mount
    const interval = setInterval(triggerFeed, 20000); // re-trigger every 20s
    return () => clearInterval(interval);
  }, [session]);

  // Build live ticker data from MarketDataContext
  const liveTickerData = [
    { symbol: 'NIFTY 50',   value: indices['NIFTY 50'],   isIndex: true },
    { symbol: 'BANKNIFTY',  value: indices['BANKNIFTY'],  isIndex: true },
    { symbol: 'SENSEX',     value: indices['SENSEX'],      isIndex: true },
    { symbol: 'RELIANCE',   value: ticks['RELIANCE'],     isIndex: false },
    { symbol: 'TCS',        value: ticks['TCS'],          isIndex: false },
    { symbol: 'HDFCBANK',   value: ticks['HDFCBANK'],     isIndex: false },
    { symbol: 'INFY',       value: ticks['INFY'],         isIndex: false },
    { symbol: 'ICICIBANK',  value: ticks['ICICIBANK'],    isIndex: false },
    { symbol: 'WIPRO',      value: ticks['WIPRO'],        isIndex: false },
    { symbol: 'BAJFINANCE', value: ticks['BAJFINANCE'],   isIndex: false },
  ].map((item) => {
    if (!item.value) return { symbol: item.symbol, ltp: '—', change: '—', pct: '—', positive: true };
    const price = item.isIndex
      ? (item.value as { value: number }).value
      : (item.value as { ltp: number }).ltp;
    const change = item.isIndex
      ? (item.value as { change: number }).change
      : (item.value as { change: number }).change;
    const pct = item.isIndex
      ? (item.value as { pct: number }).pct
      : (item.value as { pct: number }).pct;
    return {
      symbol: item.symbol,
      ltp: price?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) ?? '—',
      change: change >= 0 ? `+${change.toFixed(2)}` : change.toFixed(2),
      pct: pct >= 0 ? `+${pct.toFixed(2)}%` : `${pct.toFixed(2)}%`,
      positive: change >= 0,
    };
  });

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleLogout = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
      router.refresh();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className="flex-shrink-0 bg-secondary border-b border-border">
      {/* Ticker Strip */}
      <div className="overflow-hidden bg-background border-b border-border py-1">
        <div className="flex ticker-scroll whitespace-nowrap">
          {[...liveTickerData, ...liveTickerData]?.map((item, i) => (
            <span key={`ticker-${i}-${item?.symbol}`} className="inline-flex items-center gap-1.5 px-4 text-xs font-tabular">
              <span className="text-muted-foreground font-medium">{item?.symbol}</span>
              <span className="text-foreground font-semibold">{item?.ltp}</span>
              <span className={item?.positive ? 'text-positive' : 'text-negative'}>
                {item?.change} ({item?.pct})
              </span>
              <span className="text-border mx-1">|</span>
            </span>
          ))}
        </div>
      </div>
      {/* Main Topbar */}
      <div className="flex items-center justify-between px-3 sm:px-4 lg:px-6 h-12 gap-2">
        {/* Search */}
        <div className="relative flex-1 max-w-[180px] sm:max-w-xs">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search stocks…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              className="w-full bg-muted border border-border rounded-md pl-8 pr-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
            />
          </div>
          {searchOpen && searchQuery && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-xl z-50 overflow-hidden fade-in">
              {['RELIANCE', 'RELIANCEPP', 'RELIANCEETF']?.map((s) => (
                <button key={`search-${s}`} className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted text-foreground text-left transition-colors">
                  <span className="font-semibold">{s}</span>
                  <span className="text-muted-foreground">NSE</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3">
          {/* IST Clock */}
          <div className="hidden lg:flex items-center gap-1 text-2xs text-muted-foreground font-tabular">
            <Clock size={11} />
            <span suppressHydrationWarning>{istTime || '--:--:-- IST'}</span>
          </div>

          {/* Live / Simulated data indicator */}
          <div className={`hidden sm:flex items-center gap-1 px-2 py-1 rounded text-2xs font-semibold ${isLive ? 'bg-positive-subtle text-positive' : 'bg-muted text-muted-foreground'}`}>
            <Radio size={10} className={isLive ? 'animate-pulse' : ''} />
            <span>{isLive ? 'LIVE' : 'SIM'}</span>
          </div>

          {/* Market Status */}
          <div className={`flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-1 rounded text-2xs font-semibold ${SESSION_STYLES[session]}`}>
            {session === 'OPEN' ? <Wifi size={10} /> : <WifiOff size={10} />}
            <span className="hidden sm:inline">{SESSION_LABELS[session]}</span>
            <span className="sm:hidden">{session === 'OPEN' ? 'OPEN' : 'CLOSED'}</span>
          </div>

          {/* Notifications */}
          <button className="relative p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all touch-manipulation min-w-[32px] min-h-[32px] flex items-center justify-center">
            <Bell size={16} />
            <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-warning rounded-full" />
          </button>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              onBlur={() => setTimeout(() => setUserMenuOpen(false), 150)}
              className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 py-1 rounded-md hover:bg-muted transition-all touch-manipulation min-h-[32px]"
            >
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xs font-bold text-primary">{initials}</span>
              </div>
              <span className="text-xs font-medium text-foreground hidden sm:block">{displayName}</span>
              <ChevronDown size={12} className="text-muted-foreground hidden sm:block" />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-md shadow-xl z-50 min-w-[160px] overflow-hidden fade-in">
                <div className="px-3 py-2 border-b border-border">
                  <p className="text-xs font-semibold text-foreground truncate">{displayName}</p>
                  <p className="text-2xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-negative hover:bg-negative-subtle transition-colors touch-manipulation"
                >
                  <LogOut size={12} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}